import { publicApi } from "@api";
import GetCodeButton, { GetCodeButtonRef } from "@components/inputs/GetCodeButton/GetCodeButton";
import PasswordTextField from "@components/inputs/PasswordTextField/PasswordTextField";
import { usePostLoginCheck } from "@components/utils/Auth/hooks";
import { RegisterCallbackProps } from "@components/utils/Auth/types/auth-props";
import { envConfig } from "@configs/env";
import { SEON_FEATURE } from "@configs/seon";

import { OTPMethod, OTPSource, RegistrationMethod, useStatsigTracking } from "@configs/statsig";

import { ValidatorSchema } from "@constants";
import { zodResolver } from "@hookform/resolvers/zod";
import useCaptureFormValidationErrors from "@hooks/useCaptureFormValidationErrors";
import useContactLinks from "@hooks/useContactLinks";
import { useLoginSubmit } from "@hooks/useLoginSubmit";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { useSeonSession } from "@hooks/useSeonSession";
import { persistCredential } from "@utils/credentials";
import { resolveAuthResponseError } from "@utils/error-resolvers";
import { getOTPCodeSentMessage } from "@utils/message";
import { clearReferralCode, getReferralCode } from "@utils/referral";
import {
  APIError,
  APIErrorCodes,
  APIValidationError,
  Button,
  ErrorResponse,
  QueryError,
  TextField,
  standardizePhoneNumber,
  useHandleApiResponse,
} from "kz-ui-sdk";
import moment from "moment";
import { useCallback, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { MdMail, MdPerson, MdPhoneAndroid } from "react-icons/md";
import { z } from "zod";
import { AuthSocial } from "../../AuthSocial";

export type RegisterFormType = {
  username: string;
  password: string;
  phone: string;
  sms: string;
};

const inputClasses = {
  "input&": "w-full !px-0 !pl-11 text-start",
  "iconContainer&": "!left-3 !top-[10px]",
};

const isValidationError = (error: APIError): error is APIValidationError => {
  return error.message === APIErrorCodes.ValidationError;
};

const getErrorMessages = (error: QueryError<ErrorResponse>) => {
  const errorData = error?.data?.error;
  if (errorData && isValidationError(errorData)) {
    return {
      username: errorData?.errors?.accessKey?.msg,
      phone: errorData?.errors?.phone?.msg,
    };
  }
  return {};
};

interface RegisterUPProps extends RegisterCallbackProps {}

const RegisterUP = ({
  onRegisterSuccess,
  onLoginSuccess,
  onPostLoginCheckFailed,
  onPostLoginCheckPassed,
  deferUpdateToken,
}: RegisterUPProps) => {
  const { phoneMinLength, phoneMaxLength, phonePrefix, phoneStartWith, phoneSimpleError, showPhoneInputPrefix } =
    useMemberSettings();
  const { t, i18n } = useTranslation();
  const {
    trackOTPSent,
    trackOTPRequestFailed,
    trackRegisterFormSubmit,
    trackRegistrationComplete,
    trackRegistrationFailed,
    trackOTPVerified,
    trackOTPFailedAttempt,
    trackRegisterFormFocus,
    trackOTPRequest,
  } = useStatsigTracking();
  const getCodeButtonRef = useRef<GetCodeButtonRef>(null);
  const formStartTime = useRef<number>(Date.now());
  const otpSentTime = useRef<number | null>(null);
  const hasRegisterFormFocusedRef = useRef<boolean>(false);
  const postLoginCheck = usePostLoginCheck({
    onLoginSuccess,
    onPostLoginCheckPassed,
    onPostLoginCheckFailed,
    redirectHome: false,
  });
  const [createRegisterMutation, { isLoading: isCreateRegisterLoading }] = publicApi.useCreateRegisterMutation();
  const [verifyOTP, { isLoading: isVerifyingOTP }] = publicApi.useVerifyOTPMutation();
  const [register, { isLoading: isRegistering }] = publicApi.useSubmitRegisterMutation();
  const { handleLogin, isLoading: isLoginLoading } = useLoginSubmit({
    onLoginSuccess: postLoginCheck,
    deferUpdateToken,
    suppressLoginTrack: true,
  });

  const { getSession: getSeonSession, isFetching: isFetchingSeonSession } = useSeonSession(SEON_FEATURE.REGISTER, {
    autoInit: true,
  });

  const { openContact } = useContactLinks();

  const handleOpenContactLink = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      openContact();
    },
    [openContact],
  );

  const registerFormSchema = useMemo(() => {
    return z.object({
      username: ValidatorSchema.username,
      password: ValidatorSchema.registerPassword,
      phone: ValidatorSchema.phone({
        minLength: phoneMinLength,
        maxLength: phoneMaxLength,
        startWith: phoneStartWith,
        simpleError: phoneSimpleError,
        msgType: "digits",
      }),
      sms: ValidatorSchema.otp,
    });
  }, [phoneMinLength, phoneMaxLength, phoneStartWith, phoneSimpleError]);

  const {
    handleSubmit: handleFormSubmit,
    register: formRegister,
    watch,
    formState: { errors: formErrors },
    trigger,
    getValues,
    setError,
  } = useForm<RegisterFormType>({
    mode: "onChange",
    resolver: zodResolver(registerFormSchema),
  });

  const { handleApiResponse } = useHandleApiResponse({ toast });

  // Capture form validation errors
  useCaptureFormValidationErrors(formErrors, {
    watch,
    feature: "register",
  });

  const handleGetCode = async () => {
    return new Promise((resolve, reject) => {
      trigger(["username", "password", "phone"]).then((isFormValid) => {
        if (!isFormValid) {
          reject(new Error("Form is not valid"));
          return;
        }

        const { phone, username, password } = getValues();
        trackOTPRequest(OTPMethod.SMS, { source: OTPSource.REGISTER });
        createRegisterMutation({
          phone: standardizePhoneNumber(phone, phonePrefix),
          accountId: envConfig.accountId,
          accessKey: username.toLowerCase(),
          password,
        }).then((response) => {
          handleApiResponse(response, {
            toastSuccess: false,
            toastError: false,
            onSuccess: (otpRes) => {
              // Track OTP sent success with analytics-rich metadata
              trackOTPSent(otpRes.method as OTPMethod, { source: OTPSource.REGISTER });

              toast.success(getOTPCodeSentMessage(otpRes.method, i18n.language, t));
              const nextRequestAt = otpRes.nextRequestAt;
              const remainingSeconds = moment(nextRequestAt).diff(moment(), "seconds");
              getCodeButtonRef.current?.startTimer(remainingSeconds);
              resolve(otpRes);
            },
            onError: (error) => {
              const message = resolveAuthResponseError(error, t);
              if (message) {
                toast.error(message);
              }

              trackOTPRequestFailed(error, { source: OTPSource.REGISTER });

              reject(error);
            },
          });
        });
      });
    });
  };

  const handleOnSubmit = async (data: RegisterFormType) => {
    const { phone, sms: code, password, username } = data;

    // Call verify OTP
    const otpRes = await verifyOTP({
      phone: standardizePhoneNumber(phone, phonePrefix),
      code: code,
      accountId: envConfig.accountId,
    });

    handleApiResponse(otpRes, {
      toastError: false,
      toastSuccess: false,
      onError: (error) => {
        trackOTPFailedAttempt(error, { source: OTPSource.REGISTER });

        if (error?.data?.error?.message) {
          setError("sms", { message: error?.data?.error.message });
        }
      },
    });

    if (otpRes?.error) return;

    // Track OTP verification success with analytics data
    trackOTPVerified(OTPMethod.SMS, {
      timeToVerify: otpSentTime.current ? Math.round((Date.now() - otpSentTime.current) / 1000) : undefined,
      source: OTPSource.REGISTER,
    });

    // Track form submission only after OTP verification success
    trackRegisterFormSubmit(RegistrationMethod.USERNAME_PASSWORD, {
      timeSpentOnForm: Math.round((Date.now() - formStartTime.current) / 1000),
    });

    // Call register
    const referralCode = getReferralCode();
    const standardizedPhone = standardizePhoneNumber(phone, phonePrefix);
    const seonSession = await getSeonSession();
    const registerRes = await register({
      accountId: envConfig.accountId,
      phone: standardizedPhone,
      password,
      accessKey: username.toLowerCase(),
      code,
      referralCode,
      seonSession: seonSession,
    });

    handleApiResponse(registerRes, {
      toastSuccess: false,
      toastError: false,
      onSuccess: () => {
        // Track registration completion with comprehensive analytics
        trackRegistrationComplete(RegistrationMethod.USERNAME_PASSWORD, {
          member: registerRes?.data?.member,
        });

        toast.success(t("Register successfully!"));
        if (referralCode) {
          clearReferralCode();
        }
        handleLogin(data.username, data.password);
        persistCredential({
          phone: standardizedPhone,
        });
        onRegisterSuccess?.();
      },
      onError: (error) => {
        const errorMessages = getErrorMessages(error);

        if (errorMessages.username) {
          setError("username", { message: errorMessages.username });
        }
        if (errorMessages.phone) {
          setError("phone", { message: errorMessages.phone });
        }
        if (!errorMessages.username && !errorMessages.phone && error?.data?.error?.message) {
          toast.error(error?.data?.error?.message);
        }
        trackRegistrationFailed(RegistrationMethod.USERNAME_PASSWORD, error);
      },
    });
  };

  const isLoading = isLoginLoading || isVerifyingOTP || isRegistering || isFetchingSeonSession;

  return (
    <>
      <form
        id="form-register-username-password"
        name="form-register-username-password"
        onSubmit={handleFormSubmit(handleOnSubmit)}
        className="flex w-full flex-col gap-y-3"
        autoComplete="off"
        onFocus={() => {
          if (!hasRegisterFormFocusedRef.current) {
            trackRegisterFormFocus();
            hasRegisterFormFocusedRef.current = true;
          }
        }}
      >
        <TextField
          {...formRegister("username")}
          error={!!formErrors.username}
          helperText={t(formErrors.username?.message as string)}
          placeholder={t("username")}
          icon={<MdPerson className="text-2xl text-content-base" />}
          classes={inputClasses}
          size="sm"
          autoComplete="new-password"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          aria-label="Register username input username-password"
        />
        <PasswordTextField
          {...formRegister("password")}
          error={!!formErrors.password}
          helperText={t(formErrors.password?.message as string)}
          placeholder={t("password")}
          classes={inputClasses}
          buttonClass="!top-[10px]"
          size="sm"
          autoComplete="new-password"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          aria-label="Register password input username-password"
        />
        <TextField
          {...formRegister("phone")}
          error={!!formErrors.phone}
          helperText={t(formErrors.phone?.message ?? "", {
            minLength: phoneMinLength,
            maxLength: phoneMaxLength,
            startWith: Array.isArray(phoneStartWith) ? phoneStartWith.join(", ") : "0",
          })}
          placeholder={t("Phone number")}
          type="number"
          inputMode="numeric"
          icon={<MdPhoneAndroid className="text-2xl text-content-base" />}
          classes={{
            ...inputClasses,
            "input&": inputClasses["input&"] + (showPhoneInputPrefix ? " !pl-20" : ""),
            "prefixContainer&": showPhoneInputPrefix ? "absolute left-11 top-[10px] text-content-base" : "",
          }}
          size="sm"
          autoComplete="new-password"
          aria-label="Register phone input username-password"
          prefix={showPhoneInputPrefix ? phonePrefix : undefined}
        />
        <div className="relative">
          <TextField
            {...formRegister("sms")}
            error={!!formErrors.sms}
            helperText={t(formErrors.sms?.message as string)}
            placeholder={t("enter code")}
            type="text"
            icon={<MdMail className="text-2xl text-content-base" />}
            classes={inputClasses}
            size="sm"
            autoComplete="one-time-code"
            inputMode="numeric"
            helperTextClasses={{
              classes: {
                "errorText&": "!h-4",
              },
            }}
            aria-label="Register code input username-password"
          />
          <GetCodeButton
            onGetCode={handleGetCode}
            className="absolute right-3 top-2.5"
            classes={{
              "root&": "w-fit px-2 !h-6",
            }}
            loading={isCreateRegisterLoading}
            ref={getCodeButtonRef}
          />
          {formErrors.sms?.message === "otp is required. click on get code" && (
            <div className="text-right text-xs text-error-base">
              {t("didnt receive")} {""}
              <button
                onClick={handleOpenContactLink}
                className="underline"
                aria-label="Register contact cs username-password"
              >
                {t("contact cs")}
              </button>
            </div>
          )}
        </div>
        <Button
          variant="primary"
          type="submit"
          loading={isLoading}
          disabled={isLoading}
          size="lg"
          className="mt-2"
          aria-label="Register submit username-password"
        >
          {t("Next")}
        </Button>
      </form>

      {/* SOCIAL AUTH */}
      <AuthSocial
        mode="register"
        title={t("or")}
        onLoginSuccess={postLoginCheck}
        className="mt-2"
      />
    </>
  );
};

export default RegisterUP;
